import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { buildCallInsByStaffingDate, callInStaffingDateKey, filterCallIns, STAFFING_DATE_KEY_FORMAT } from "../utils/callIns";
import type { CalledOffShift } from "../utils/models";
import { CALL_INS_PAGE_SIZE, useGetLimitedCallOffs } from "./useGetLimitedCallOffs";

/**
 * The backend filters `from_date` / `to_date` against the shift's `start_time`, a timestamptz compared
 * against a bare date — so the cut lands on a UTC midnight, not the department's staffing-day boundary.
 * A night shift can therefore sit one calendar day either side of the day we asked for. Pad the window
 * by a day in each direction and let `callInStaffingDateKey` make the real decision in dept-local time.
 */
const DAY_WINDOW_PAD_DAYS = 1;

/**
 * Every call-in of a single nurse, grouped by the staffing day of the shift it removed — the shape the
 * profile calendar needs to mark a cell.
 *
 * Deliberately fetches the nurse's *whole* history with the same parameters `LastCallIns` uses, rather
 * than a window around the visible weeks: the profile renders both surfaces, so sharing the query key
 * means TanStack Query serves both from one request, and scrolling the calendar back through years of
 * history never triggers another. (`CALL_INS_PAGE_SIZE` is shared for exactly that reason — the dedupe
 * only holds while the keys match.)
 */
export const useNurseCallInsByStaffingDate = ({ nurseId, enabled = true }: { nurseId: number | undefined; enabled?: boolean }) => {
	const { chosenDepartment } = useChosenDepartment();
	const { combinedData, isLoading, isError } = useGetLimitedCallOffs({
		nurseIds: enabled && nurseId ? [nurseId] : [],
		page: 1,
		pageSize: CALL_INS_PAGE_SIZE,
	});

	// The endpoint returns every removal; only the nurse-initiated ones get a marker (see `filterCallIns`).
	const callInsByDate = useMemo(
		() => buildCallInsByStaffingDate(filterCallIns(combinedData.results), chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes),
		[combinedData.results, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	return { callInsByDate, isLoading, isError };
};

/**
 * The facility's call-ins for one staffing day, earliest shift first — the manager-facing "who called
 * out today" list.
 *
 * `date` is a staffing day, not a wall clock instant; the fetch window is padded (see
 * `DAY_WINDOW_PAD_DAYS`) and then narrowed in dept-local time so a night shift lands on the day it is
 * worked rather than the day it crosses into.
 */
export const useDepartmentCallInsForDay = ({ date, enabled = true }: { date: DateTime; enabled?: boolean }) => {
	const { chosenDepartment } = useChosenDepartment();
	const dayKey = date.toFormat(STAFFING_DATE_KEY_FORMAT);

	const { combinedData, isLoading, isError } = useGetLimitedCallOffs({
		page: 1,
		pageSize: CALL_INS_PAGE_SIZE,
		fromDate: date.minus({ days: DAY_WINDOW_PAD_DAYS }),
		toDate: date.plus({ days: DAY_WINDOW_PAD_DAYS + 1 }),
		enabled,
	});

	const callIns = useMemo(
		() =>
			filterCallIns(combinedData.results)
				.filter((callIn) => callInStaffingDateKey(callIn, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes) === dayKey)
				.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis()),
		[combinedData.results, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes, dayKey],
	);

	return { callIns, isLoading, isError };
};
